import apiClient from './apiClient';

export type InspectionType = 'quality' | 'safety' | 'material' | 'permit' | 'handover' | 'workmanship' | 'other';
export type InspectionStatus = 'planned' | 'scheduled' | 'in_progress' | 'passed' | 'failed' | 'cancelled';
export type ChecklistItemStatus = 'pending' | 'pass' | 'fail' | 'na';
export type NcrSeverity = 'low' | 'medium' | 'high' | 'critical';
export type NcrStatus = 'open' | 'containment' | 'corrective_action' | 'verification' | 'closed' | 'void';

export interface ChecklistItem {
  id: string;
  inspectionId: string;
  itemCode: string;
  description: string;
  status: ChecklistItemStatus;
  notes: string | null;
  sortOrder: number;
  evidenceDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionRow {
  id: string;
  projectId: string;
  taskId: string | null;
  inspectionNumber: string;
  title: string;
  inspectionType: InspectionType;
  status: InspectionStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  inspectorUserId: string | null;
  teamId: string | null;
  resultSummary: string | null;
  score: number | null;
  documentId: string | null;
  checklistItems?: ChecklistItem[];
  ncrs?: NcrRow[];
  createdAt: string;
  updatedAt: string;
}

export interface NcrRow {
  id: string;
  projectId: string;
  inspectionId: string | null;
  taskId: string | null;
  defectId: string | null;
  ncrNumber: string;
  title: string;
  description: string | null;
  severity: NcrSeverity;
  status: NcrStatus;
  rootCause: string | null;
  correctiveAction: string | null;
  ownerUserId: string | null;
  dueDate: string | null;
  closedAt: string | null;
  documentId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InspectionPayload {
  projectId: string;
  taskId?: string | null;
  inspectionNumber: string;
  title: string;
  inspectionType: InspectionType;
  status?: InspectionStatus;
  scheduledAt?: string | null;
  inspectorUserId?: string | null;
  teamId?: string | null;
  resultSummary?: string | null;
  score?: number | null;
  documentId?: string | null;
}

export interface NcrPayload {
  projectId: string;
  inspectionId?: string | null;
  taskId?: string | null;
  defectId?: string | null;
  ncrNumber: string;
  title: string;
  description?: string | null;
  severity?: NcrSeverity;
  status?: NcrStatus;
  rootCause?: string | null;
  correctiveAction?: string | null;
  ownerUserId?: string | null;
  dueDate?: string | null;
  documentId?: string | null;
}

class InspectionsService {
  async list(params: {
    projectId?: string;
    taskId?: string;
    inspectorUserId?: string;
    teamId?: string;
    status?: InspectionStatus;
    inspectionType?: InspectionType;
    scheduledBefore?: string;
    scheduledAfter?: string;
  } = {}): Promise<InspectionRow[]> {
    const response: any = await apiClient.get('/inspections', { params });
    return response.data;
  }

  async create(payload: InspectionPayload): Promise<InspectionRow> {
    const response: any = await apiClient.post('/inspections', payload);
    return response.data;
  }

  async update(id: string, payload: Partial<InspectionPayload>): Promise<InspectionRow> {
    const response: any = await apiClient.patch(`/inspections/${id}`, payload);
    return response.data;
  }

  async complete(id: string, payload: { status: 'passed' | 'failed' | 'cancelled'; resultSummary?: string | null; score?: number | null }): Promise<InspectionRow> {
    const response: any = await apiClient.post(`/inspections/${id}/complete`, payload);
    return response.data;
  }

  async addChecklistItem(inspectionId: string, payload: { itemCode: string; description: string; status?: ChecklistItemStatus; notes?: string | null; sortOrder?: number }): Promise<ChecklistItem> {
    const response: any = await apiClient.post(`/inspections/${inspectionId}/checklist-items`, payload);
    return response.data;
  }

  async updateChecklistItem(itemId: string, payload: Partial<{ itemCode: string; description: string; status: ChecklistItemStatus; notes: string | null; sortOrder: number }>): Promise<ChecklistItem> {
    const response: any = await apiClient.patch(`/inspections/checklist-items/${itemId}`, payload);
    return response.data;
  }

  async deleteChecklistItem(itemId: string): Promise<void> {
    await apiClient.delete(`/inspections/checklist-items/${itemId}`);
  }

  async listNcrs(params: {
    projectId?: string;
    inspectionId?: string;
    taskId?: string;
    defectId?: string;
    ownerUserId?: string;
    severity?: NcrSeverity;
    status?: NcrStatus;
    dueBefore?: string;
    dueAfter?: string;
  } = {}): Promise<NcrRow[]> {
    const response: any = await apiClient.get('/inspections/ncrs', { params });
    return response.data;
  }

  async createNcr(payload: NcrPayload): Promise<NcrRow> {
    const response: any = await apiClient.post('/inspections/ncrs', payload);
    return response.data;
  }

  async updateNcr(id: string, payload: Partial<NcrPayload>): Promise<NcrRow> {
    const response: any = await apiClient.patch(`/inspections/ncrs/${id}`, payload);
    return response.data;
  }

  async closeNcr(id: string): Promise<NcrRow> {
    const response: any = await apiClient.post(`/inspections/ncrs/${id}/close`);
    return response.data;
  }
}

export default new InspectionsService();
